import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, merge, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, switchMap, catchError, filter } from 'rxjs/operators';
import { NetworkQuality } from '../models';

/**
 * Network visibility state interface
 */
export interface NetworkVisibilityState {
  readonly isOnline: boolean;
  readonly isVisible: boolean;
  readonly isFocused: boolean;
  readonly quality: NetworkQuality;
  readonly lastCheck: Date;
}

/**
 * Simplified network quality detector with memory safety and visibility awareness
 */
@Injectable({
  providedIn: 'root'
})
export class NetworkQualityService implements OnDestroy {
  private readonly destroy$ = new BehaviorSubject<void>(undefined);
  private readonly quality$ = new BehaviorSubject<NetworkQuality>(NetworkQuality.GOOD);
  private readonly visibilitySubject$ = new BehaviorSubject<NetworkVisibilityState>({
    isOnline: navigator.onLine,
    isVisible: !document.hidden,
    isFocused: document.hasFocus(),
    quality: NetworkQuality.GOOD,
    lastCheck: new Date()
  });

  private activeControllers: Set<AbortController> = new Set();

  private readonly PING_URL = 'https://www.cloudflare.com/favicon.ico';
  private readonly QUALITY_THRESHOLDS = {
    excellent: 100,  // < 100ms
    good: 300,       // 100-300ms
    poor: 1000       // 300-1000ms, >1000ms = offline
  };

  private readonly CHECK_INTERVAL = 30000; // 30 seconds
  private readonly PING_TIMEOUT = 5000;    // 5 seconds
  private readonly VISIBILITY_DEBOUNCE = 2000; // 2 seconds

  public readonly networkQuality$: Observable<NetworkQuality> = this.quality$.pipe(
    distinctUntilChanged(),
    takeUntil(this.destroy$)
  );

  public readonly visibilityState$: Observable<NetworkVisibilityState> = this.visibilitySubject$.pipe(
    distinctUntilChanged((prev, curr) =>
      prev.isOnline === curr.isOnline &&
      prev.isVisible === curr.isVisible &&
      prev.isFocused === curr.isFocused &&
      prev.quality === curr.quality
    ),
    takeUntil(this.destroy$)
  );

  constructor() {
    this.initializeNetworkMonitoring();
    this.initializeVisibilityMonitoring();
  }  /**
   * Initialize network monitoring with cleanup
   */
  private initializeNetworkMonitoring(): void {
    // Basic online/offline detection
    merge(
      fromEvent(window, 'online'),
      fromEvent(window, 'offline')
    ).pipe(
      debounceTime(1000),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateVisibilityState({ isOnline: navigator.onLine });

      if (navigator.onLine) {
        this.performQualityCheck();
      } else {
        this.updateQuality(NetworkQuality.OFFLINE);
      }
    });

    // Periodic quality checks
    timer(0, this.CHECK_INTERVAL).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.performQualityCheck()),
      catchError(() => [NetworkQuality.POOR])
    ).subscribe();
  }

  /**
   * Initialize visibility monitoring for page visibility and focus
   */
  private initializeVisibilityMonitoring(): void {
    // Page visibility monitoring
    fromEvent(document, 'visibilitychange').pipe(
      debounceTime(this.VISIBILITY_DEBOUNCE),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      const isVisible = !document.hidden;
      this.updateVisibilityState({ isVisible });

      // Trigger quality check when page becomes visible
      if (isVisible && navigator.onLine) {
        this.performQualityCheck();
      }
    });

    // Window focus monitoring
    merge(
      fromEvent(window, 'focus'),
      fromEvent(window, 'blur')
    ).pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      const isFocused = document.hasFocus();
      this.updateVisibilityState({ isFocused });

      // Trigger quality check when window gains focus
      if (isFocused && navigator.onLine && !document.hidden) {
        this.performQualityCheck();
      }
    });
  }

  /**
   * Perform a single quality check
   */
  private performQualityCheck(): Observable<NetworkQuality> {
    return new Observable<NetworkQuality>(observer => {
      if (!navigator.onLine) {
        observer.next(NetworkQuality.OFFLINE);
        observer.complete();
        return;
      }

      const startTime = performance.now();
      const controller = new AbortController();
      this.activeControllers.add(controller);

      // Timeout handling
      const timeoutId = setTimeout(() => {
        controller.abort();
        this.activeControllers.delete(controller);
        observer.next(NetworkQuality.POOR);
        observer.complete();
      }, this.PING_TIMEOUT);

      fetch(this.PING_URL, {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache',
        signal: controller.signal
      })
      .then(() => {
        clearTimeout(timeoutId);
        this.activeControllers.delete(controller);
        const rtt = performance.now() - startTime;
        const quality = this.calculateQualityFromRTT(rtt);
        this.updateQuality(quality);
        observer.next(quality);
        observer.complete();
      })
      .catch(() => {
        clearTimeout(timeoutId);
        this.activeControllers.delete(controller);
        const quality = navigator.onLine ? NetworkQuality.POOR : NetworkQuality.OFFLINE;
        this.updateQuality(quality);
        observer.next(quality);
        observer.complete();
      });

      // Cleanup function
      return () => {
        clearTimeout(timeoutId);
        this.activeControllers.delete(controller);
        controller.abort();
      };
    });
  }

  /**
   * Calculate quality from round-trip time
   */
  private calculateQualityFromRTT(rtt: number): NetworkQuality {
    if (rtt < this.QUALITY_THRESHOLDS.excellent) {
      return NetworkQuality.EXCELLENT;
    } else if (rtt < this.QUALITY_THRESHOLDS.good) {
      return NetworkQuality.GOOD;
    } else if (rtt < this.QUALITY_THRESHOLDS.poor) {
      return NetworkQuality.POOR;
    } else {
      return NetworkQuality.OFFLINE;
    }
  }

  /**
   * Update quality with validation
   */
  private updateQuality(quality: NetworkQuality): void {
    if (this.quality$.value !== quality) {
      this.quality$.next(quality);
      this.updateVisibilityState({ quality });
    }
  }

  /**
   * Update visibility state with partial updates
   */
  private updateVisibilityState(updates: Partial<NetworkVisibilityState>): void {
    const currentState = this.visibilitySubject$.value;
    this.visibilitySubject$.next({
      ...currentState,
      ...updates,
      lastCheck: new Date()
    });
  }

  /**
   * Get current network quality
   */
  public getCurrentQuality(): NetworkQuality {
    return this.quality$.value;
  }

  /**
   * Get current visibility state
   */
  public getCurrentVisibilityState(): NetworkVisibilityState {
    return this.visibilitySubject$.value;
  }

  /**
   * Check if network is suitable for WebSocket with hibernation awareness
   */
  public isSuitableForWebSocket(): boolean {
    const quality = this.getCurrentQuality();
    const state = this.getCurrentVisibilityState();

    // After hibernation or long offline period, be more conservative
    const timeSinceLastCheck = Date.now() - state.lastCheck.getTime();
    if (timeSinceLastCheck > 60000) { // 1 minute gap suggests hibernation
      // Only allow excellent connections immediately after hibernation
      return quality === NetworkQuality.EXCELLENT;
    }

    return quality === NetworkQuality.EXCELLENT || quality === NetworkQuality.GOOD;
  }

  /**
   * Enhanced hibernation-aware connection suitability check
   */
  public isSuitableForWebSocketWithDelay(): Observable<boolean> {
    return new Observable(observer => {
      const state = this.getCurrentVisibilityState();
      const timeSinceLastCheck = Date.now() - state.lastCheck.getTime();

      // If long gap (hibernation), wait and recheck with multiple attempts
      if (timeSinceLastCheck > 30000) {
        this.performStabilityCheck().subscribe(isStable => {
          observer.next(isStable);
          observer.complete();
        });
      } else {
        observer.next(this.isSuitableForWebSocket());
        observer.complete();
      }
    });
  }

  /**
   * Perform multiple quality checks to ensure network stability after hibernation
   */
  private performStabilityCheck(): Observable<boolean> {
    return new Observable(observer => {
      let checkCount = 0;
      const maxChecks = 3;
      const checkInterval = 1500; // 1.5 seconds between checks
      let consecutiveGoodChecks = 0;

      const performCheck = () => {
        this.performQualityCheck().subscribe(quality => {
          checkCount++;
          
          if (quality === NetworkQuality.EXCELLENT || quality === NetworkQuality.GOOD) {
            consecutiveGoodChecks++;
          } else {
            consecutiveGoodChecks = 0;
          }

          // Success if we have 2 consecutive good checks or all checks are done
          if (consecutiveGoodChecks >= 2 || checkCount >= maxChecks) {
            observer.next(consecutiveGoodChecks >= 2);
            observer.complete();
            return;
          }

          // Schedule next check
          setTimeout(performCheck, checkInterval);
        });
      };

      // Start first check after initial stabilization delay
      setTimeout(performCheck, 2000);
    });
  }

  /**
   * Check if application is actively being used
   */
  public isApplicationActive(): boolean {
    const state = this.getCurrentVisibilityState();
    return state.isOnline && state.isVisible && state.isFocused;
  }

  /**
   * Check if application is visible (but may not be focused)
   */
  public isApplicationVisible(): boolean {
    const state = this.getCurrentVisibilityState();
    return state.isOnline && state.isVisible;
  }

  /**
   * Get recommended reconnection delay based on quality
   */
  public getRecommendedDelay(): number {
    switch (this.getCurrentQuality()) {
      case NetworkQuality.EXCELLENT:
        return 1000;  // 1 second
      case NetworkQuality.GOOD:
        return 3000;  // 3 seconds
      case NetworkQuality.POOR:
        return 10000; // 10 seconds
      case NetworkQuality.OFFLINE:
      default:
        return 30000; // 30 seconds
    }
  }

  /**
   * Cleanup resources
   */
  ngOnDestroy(): void {
    // Cancel all active fetch requests
    this.activeControllers.forEach(controller => controller.abort());
    this.activeControllers.clear();

    this.destroy$.next();
    this.destroy$.complete();
    this.quality$.complete();
    this.visibilitySubject$.complete();
  }
}
